/**
 * AG04Dashboard — Interface OmniHub pour l'agent Webmail amo@.
 *
 * 4 onglets :
 *  - Queue           : file mails traités (classification + action prise)
 *  - Revue manuelle  : messages classifiés inconnu / source non actionnable
 *  - Alertes         : alertes à traiter (open / resolved / all)
 *  - Historique      : tous les mails traités, audit
 *
 * Endpoints backend (cf. backend/email_hub/ag04_routes.py) :
 *  - GET  /api/metier/ag04/queue
 *  - GET  /api/metier/ag04/manual-review
 *  - POST /api/metier/ag04/reclassify/{message_id}
 *  - GET  /api/metier/ag04/alerts?status={open|resolved|all}
 *  - POST /api/metier/ag04/alerts/{alert_id}/resolve
 *
 * Tenant TEF only (X-Tenant-ID forcé via getTenantHeaders).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Inbox, AlertTriangle, History, RefreshCw, Loader2,
  Edit3, Check, X,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { StatChip } from '../../../components/ui/oh-tinted';
import { toast } from 'sonner';
import { getTenantHeaders, getCurrentTenantSlug } from '../../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SOURCE_LABELS = {
  anah_plateforme: 'ANAH plateforme',
  anah_instructeur: 'ANAH instructeur',
  installateur: 'Installateur',
  client: 'Client',
  inconnu: 'Inconnu',
};

const TYPE_LABELS = {
  '1_echange': 'Échange ANAH',
  '2_question_instructeur': 'Question instructeur',
  '3_decision': 'Décision ANAH',
  '4a_choix_amo_connu': 'Choix AMO (client connu)',
  '4b_choix_amo_nouveau': 'Choix AMO (nouveau prospect)',
  '5_spam': 'Spam / hors-sujet',
  '6_question_client': 'Question client',
  inconnu: 'À classer',
};

const formatFromShort = (raw) => {
  if (!raw || raw === '—') return '—';
  // raw peut être {"email":"x","name":"y"} sérialisé OU simple string
  let val = raw;
  if (typeof raw === 'object') {
    val = raw.name || raw.email || '—';
  }
  // Parse "Name <email@x.com>" ou "Email Name"
  const m = String(val).match(/^([^<]+?)\s*<[^>]+>$/);
  if (m) return m[1].trim();
  // Si c'est juste un email, garder le local-part
  if (String(val).includes('@')) {
    const local = String(val).split('@')[0];
    return local.length < 30 ? local : local.slice(0, 27) + '…';
  }
  return String(val).slice(0, 40);
};

const formatEmailDate = (rawDate) => {
  if (!rawDate) return '—';
  // email_date peut être string (RFC 2822 / ISO) ou int (timestamp)
  let d;
  if (typeof rawDate === 'number') {
    d = new Date(rawDate * 1000);
  } else {
    d = new Date(rawDate);
  }
  if (isNaN(d.getTime())) return String(rawDate).slice(0, 16);
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const ACTION_BADGE = (taken) => {
  if (!taken) return null;
  const map = {
    comment_added: { color: 'blue', label: 'Commentaire ajouté' },
    comment_and_alert: { color: 'amber', label: 'Commentaire + alerte' },
    deduped: { color: 'purple', label: 'Doublon (24h)' },
    skipped: { color: 'purple', label: 'Ignoré' },
    manual_review: { color: 'orange', label: 'Revue manuelle' },
    vision_extracted: { color: 'green', label: 'Vision OK' },
    comment_and_alert_no_decision: { color: 'amber', label: 'Sans décision' },
    linked_ref_anah_and_alert: { color: 'cyan', label: 'Réf ANAH liée' },
    client_and_dossier_created: { color: 'green', label: 'Client+dossier créés' },
    linked_to_existing_dossier: { color: 'cyan', label: 'Rattaché dossier existant' },
    alert_prospect_entrant_pending: { color: 'orange', label: 'Prospect en attente' },
    spam_marked_for_delete: { color: 'red', label: 'Spam → corbeille' },
    alert_only_no_dossier: { color: 'orange', label: 'Alerte (à rattacher)' },
    error: { color: 'red', label: 'Erreur' },
  };
  const cfg = map[taken] || { color: 'purple', label: taken };
  return (
    <StatChip color={cfg.color} size="xs">
      {cfg.label}
    </StatChip>
  );
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AG04Dashboard = () => {
  const [tab, setTab] = useState('queue');
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState([]);
  const [manualReview, setManualReview] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertStatus, setAlertStatus] = useState('open');
  const [reclassTarget, setReclassTarget] = useState(null);
  const [reclassSource, setReclassSource] = useState('anah_plateforme');
  const [reclassType, setReclassType] = useState('1_echange');
  const [reclassOperator, setReclassOperator] = useState('');

  const headers = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return {
      ...getTenantHeaders(),
      ...(token && { Authorization: `Bearer ${token}` }),
      'Content-Type': 'application/json',
    };
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [q, mr, al] = await Promise.all([
        fetch(`${API}/api/metier/ag04/queue?limit=100`, { headers: headers() }),
        fetch(`${API}/api/metier/ag04/manual-review?limit=100`, { headers: headers() }),
        fetch(`${API}/api/metier/ag04/alerts?status=${alertStatus}&limit=100`, { headers: headers() }),
      ]);
      if (q.ok) setQueue((await q.json()).items || []);
      if (mr.ok) setManualReview((await mr.json()).items || []);
      if (al.ok) setAlerts((await al.json()).items || []);
    } catch (e) {
      toast.error('Erreur chargement AG-04 : ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [headers, alertStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submitReclassify = async () => {
    if (!reclassTarget || !reclassOperator) {
      toast.error('Opérateur requis');
      return;
    }
    try {
      const res = await fetch(
        `${API}/api/metier/ag04/reclassify/${encodeURIComponent(reclassTarget.message_id)}`,
        {
          method: 'POST', headers: headers(),
          body: JSON.stringify({
            new_source: reclassSource, new_type: reclassType,
            operator: reclassOperator,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Reclassification échouée');
      }
      toast.success('Reclassifié');
      setReclassTarget(null);
      loadAll();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const res = await fetch(
        `${API}/api/metier/ag04/alerts/${alertId}/resolve`,
        { method: 'POST', headers: headers() },
      );
      if (!res.ok) throw new Error('Échec résolution');
      toast.success('Alerte résolue');
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const decideAlert = async (alertId, decision) => {
    // Pour Type 7 invitation mandataire : décision Accepter / Refuser.
    // L'action ANAH (côté plateforme) reste manuelle — ce bouton marque
    // juste la décision côté OH et retire l'alerte de la file active.
    const label = decision === "accepted" ? "Accepter" : "Refuser";
    if (!window.confirm(`${label} l'invitation mandataire ?\n\nN'oubliez pas de confirmer côté plateforme ANAH (monprojet.anah.gouv.fr).`)) return;
    try {
      const res = await fetch(
        `${API}/api/metier/ag04/alerts/${alertId}/decide?decision=${decision}`,
        { method: 'POST', headers: headers() },
      );
      if (!res.ok) throw new Error('Échec décision');
      toast.success(decision === "accepted" ? "Invitation mandataire acceptée" : "Invitation mandataire refusée");
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const TabButton = ({ id, icon: Icon, label, count }) => (
    <button
      data-testid={`ag04-tab-${id}`}
      onClick={() => setTab(id)}
      style={{
        padding: '10px 16px', borderRadius: 8,
        background: tab === id ? 'var(--oh-stat-blue-active-bg)' : 'transparent',
        border: tab === id
          ? '1px solid var(--oh-stat-blue-active-border)'
          : '1px solid var(--oh-border)',
        color: tab === id ? 'var(--oh-stat-blue-text)' : 'var(--oh-text)',
        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
        fontWeight: 600, fontSize: 14,
      }}
    >
      <Icon size={16} />
      <span>{label}</span>
      {typeof count === 'number' && (
        <Badge style={{ background: 'var(--oh-bg-2)', color: 'var(--oh-text)', border: '1px solid var(--oh-border)' }}>
          {count}
        </Badge>
      )}
    </button>
  );

  const ItemRow = ({ item, withReclassify = false }) => {
    const cls = item.classification || {};
    const action = item.action_result || {};
    const reclass = item.manual_reclassified;
    return (
      <div
        data-testid={`ag04-item-${item.message_id}`}
        style={{
          background: 'var(--oh-bg-1)', border: '1px solid var(--oh-border)',
          borderRadius: 10, padding: 14, marginBottom: 10,
          display: 'flex', flexDirection: 'column', gap: 6,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Badge style={{ background: 'var(--oh-stat-blue-bg)', color: 'var(--oh-stat-blue-text)' }}>
                {SOURCE_LABELS[cls.source] || cls.source || '?'}
              </Badge>
              <Badge style={{ background: 'var(--oh-stat-purple-bg)', color: 'var(--oh-stat-purple-text)' }}>
                {TYPE_LABELS[cls.type] || cls.type || '?'}
              </Badge>
              {ACTION_BADGE(action.action_taken)}
              {reclass && (
                <Badge style={{ background: 'var(--oh-stat-amber-bg)', color: 'var(--oh-stat-amber-text)' }}>
                  Reclassifié
                </Badge>
              )}
              <span style={{ color: 'var(--oh-text)', opacity: 0.7, fontSize: 12, marginLeft: 'auto' }}>
                {fmtDate(item.processed_at)}
              </span>
            </div>
            <div style={{ marginTop: 6, fontSize: 14, color: 'var(--oh-text)', fontWeight: 500 }}>
              {item.email_subject || '(Sans sujet)'}
            </div>
            <div style={{ marginTop: 2, fontSize: 12, color: 'var(--oh-text)', opacity: 0.75, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <span>De : <strong>{formatFromShort(item.email_from)}</strong></span>
              {item.email_date && <span>· {formatEmailDate(item.email_date)}</span>}
              {item.email_folder && <span>· {item.email_folder}</span>}
            </div>
            {(item.matched_dossier_reference || item.matched_client_name) && (
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--oh-stat-green-text)' }}>
                → {item.matched_dossier_reference || ''}
                {item.matched_client_name && ` · ${item.matched_client_name}`}
              </div>
            )}
            {(cls.reasons_human?.length > 0 || cls.reasons?.length > 0) && (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>
                {(cls.reasons_human || cls.reasons).slice(0, 2).join(' · ')}
              </div>
            )}
          </div>
          {withReclassify && (
            <Button
              size="sm" variant="outline"
              data-testid={`ag04-reclassify-btn-${item.message_id}`}
              onClick={() => {
                setReclassTarget(item);
                setReclassSource(cls.source || 'anah_plateforme');
                setReclassType(cls.type || '1_echange');
              }}
            >
              <Edit3 size={14} style={{ marginRight: 4 }} />
              Reclassifier
            </Button>
          )}
        </div>
      </div>
    );
  };

  const AlertRow = ({ alert }) => (
    <div
      data-testid={`ag04-alert-${alert._id}`}
      style={{
        background: alert.status === 'resolved'
          ? 'var(--oh-bg-2)' : 'var(--oh-stat-amber-bg)',
        border: `1px solid var(--oh-stat-amber-border)`,
        borderRadius: 10, padding: 14, marginBottom: 10,
        opacity: alert.status === 'resolved' ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--oh-text)' }}>
            {alert.title}
          </div>
          {alert.deadline && (
            <Badge style={{ background: 'var(--oh-stat-red-bg)', color: 'var(--oh-stat-red-text)', marginTop: 4 }}>
              Deadline : {alert.deadline}
            </Badge>
          )}
          <div style={{ fontSize: 12, color: 'var(--oh-text)', opacity: 0.85, marginTop: 6, whiteSpace: 'pre-wrap' }}>
            {alert.body}
          </div>
          <div style={{ fontSize: 11, opacity: 0.6, marginTop: 6 }}>
            {fmtDate(alert.created_at)}
            {alert.resolved_at && ` — résolu ${fmtDate(alert.resolved_at)}`}
          </div>
        </div>
        {alert.status !== 'resolved' && (
          alert?.metadata?.ag04_type === "7_invitation_mandataire" ? (
            // Type 7 : 2 boutons décision (Accepter / Refuser) au lieu du
            // bouton générique "Résoudre". Marque la décision dans la DB ;
            // pas d'appel API ANAH — la confirmation côté plateforme ANAH
            // reste à faire manuellement par l'opérateur.
            <div style={{ display: 'flex', gap: 6 }}>
              <Button
                size="sm" variant="outline"
                data-testid={`ag04-accept-${alert._id}`}
                onClick={() => decideAlert(alert._id, "accepted")}
                className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
                aria-label="Accepter l'invitation mandataire"
              >
                <Check size={14} style={{ marginRight: 4 }} />
                Accepter
              </Button>
              <Button
                size="sm" variant="outline"
                data-testid={`ag04-refuse-${alert._id}`}
                onClick={() => decideAlert(alert._id, "refused")}
                className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                aria-label="Refuser l'invitation mandataire"
              >
                <X size={14} style={{ marginRight: 4 }} />
                Refuser
              </Button>
            </div>
          ) : (
            <Button
              size="sm" variant="ghost"
              data-testid={`ag04-resolve-${alert._id}`}
              onClick={() => resolveAlert(alert._id)}
            >
              <Check size={14} style={{ marginRight: 4 }} />
              Résoudre
            </Button>
          )
        )}
        {alert.status === 'resolved' && alert.decision && (
          // Une fois résolue avec décision, on affiche le badge
          <Badge style={{
            background: alert.decision === "accepted" ? "var(--oh-stat-green-bg)" : "var(--oh-stat-red-bg)",
            color: alert.decision === "accepted" ? "var(--oh-stat-green-text)" : "var(--oh-stat-red-text)",
          }}>
            {alert.decision === "accepted" ? "✓ Acceptée" : "✗ Refusée"}
          </Badge>
        )}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-4" data-testid="ag04-dashboard">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--oh-text)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Mail size={22} />
            Agent Webmail amo@ (AG-04)
          </h1>
          <p style={{ fontSize: 13, color: 'var(--oh-text)', opacity: 0.7, marginTop: 4 }}>
            Classification + matching + actions sur la boîte amo@ {getCurrentTenantSlug() === 'tef' ? 'TEF' : 'de votre agence'}.
          </p>
        </div>
        <Button
          variant="outline" size="sm" onClick={loadAll}
          data-testid="ag04-reload"
          aria-label="Rafraîchir"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          <span style={{ marginLeft: 6 }}>Rafraîchir</span>
        </Button>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <TabButton id="queue" icon={Inbox} label="Mails traités" count={queue.length} />
        <TabButton id="manual" icon={AlertTriangle} label="Revue manuelle" count={manualReview.length} />
        <TabButton id="alerts" icon={AlertTriangle} label="Alertes" count={alerts.length} />
        <TabButton id="history" icon={History} label="Historique" />
      </div>

      <div style={{ background: 'var(--oh-bg-2)', border: '1px solid var(--oh-border)', borderRadius: 12, padding: 16, minHeight: 200 }}>
        {tab === 'queue' && (
          <>
            <div style={{ marginBottom: 10, fontSize: 13, opacity: 0.75 }}>
              {queue.length} mails traités récemment.
            </div>
            {queue.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>
                Aucun mail traité — l'agent n'a rien reçu encore.
              </div>
            )}
            {queue.map((it) => <ItemRow key={it.message_id} item={it} />)}
          </>
        )}

        {tab === 'manual' && (
          <>
            <div style={{ marginBottom: 10, fontSize: 13, opacity: 0.75 }}>
              {manualReview.length} mails à classifier manuellement.
            </div>
            {manualReview.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>
                Pile vide — toutes les classifications sont confirmées.
              </div>
            )}
            {manualReview.map((it) => (
              <ItemRow key={it.message_id} item={it} withReclassify />
            ))}
          </>
        )}

        {tab === 'alerts' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 13, opacity: 0.75 }}>Statut :</span>
              <Select value={alertStatus} onValueChange={setAlertStatus}>
                <SelectTrigger style={{ width: 140 }} data-testid="ag04-alerts-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Ouvertes</SelectItem>
                  <SelectItem value="resolved">Résolues</SelectItem>
                  <SelectItem value="all">Toutes</SelectItem>
                </SelectContent>
              </Select>
              <span style={{ fontSize: 13, opacity: 0.75, marginLeft: 'auto' }}>
                {alerts.length} alertes
              </span>
            </div>
            {alerts.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>
                Aucune alerte.
              </div>
            )}
            {alerts.map((a) => <AlertRow key={a._id} alert={a} />)}
          </>
        )}

        {tab === 'history' && (
          <>
            <div style={{ marginBottom: 10, fontSize: 13, opacity: 0.75 }}>
              Audit complet : tous les mails traités (incluant dédup et erreurs).
            </div>
            {queue.length === 0 && !loading && (
              <div style={{ textAlign: 'center', padding: 40, opacity: 0.5 }}>
                Vide.
              </div>
            )}
            {queue.map((it) => <ItemRow key={it.message_id} item={it} withReclassify />)}
          </>
        )}
      </div>

      <Dialog open={!!reclassTarget} onOpenChange={(o) => !o && setReclassTarget(null)}>
        <DialogContent data-testid="ag04-reclassify-modal">
          <DialogHeader>
            <DialogTitle>Reclassifier le message</DialogTitle>
          </DialogHeader>
          {reclassTarget && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 12, opacity: 0.7, wordBreak: 'break-all' }}>
                {reclassTarget.message_id}
              </div>

              <div>
                <Label htmlFor="ag04-rc-source">Source</Label>
                <Select value={reclassSource} onValueChange={setReclassSource}>
                  <SelectTrigger id="ag04-rc-source" data-testid="ag04-rc-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ag04-rc-type">Type</Label>
                <Select value={reclassType} onValueChange={setReclassType}>
                  <SelectTrigger id="ag04-rc-type" data-testid="ag04-rc-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ag04-rc-operator">Opérateur</Label>
                <Input
                  id="ag04-rc-operator"
                  value={reclassOperator}
                  onChange={(e) => setReclassOperator(e.target.value)}
                  placeholder="Ton nom"
                  data-testid="ag04-rc-operator"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReclassTarget(null)}>
              <X size={14} style={{ marginRight: 4 }} />
              Annuler
            </Button>
            <Button onClick={submitReclassify} data-testid="ag04-rc-submit">
              <Check size={14} style={{ marginRight: 4 }} />
              Reclassifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AG04Dashboard;
